import { theme } from "antd";
import PageLayout from "../PageLayout";
import styles from "./DeleteAccount.module.css";

const { useToken } = theme;

const Section = ({ n, title, children }) => (
	<section className={styles.section}>
		<h2 className={styles.heading}>
			<span className={styles.num}>{n}</span>
			<span>{title}</span>
		</h2>
		{children}
	</section>
);

const DeleteAccount = () => {
	const { token } = useToken();

	// Feed theme tokens into the CSS module as variables — keeps all colors
	// token-driven while layout/typography lives in DeleteAccount.module.css.
	const themeVars = {
		"--da-text": token.colorText,
		"--da-secondary": token.colorTextSecondary,
		"--da-primary": token.colorPrimary,
		"--da-primary-bg": token.colorPrimaryBg,
		"--da-border": token.colorBorderSecondary,
		"--da-fill": token.colorFillTertiary,
		"--da-warning": token.colorWarning,
		"--da-warning-bg": token.colorWarningBg,
		"--da-warning-border": token.colorWarningBorder,
	};

	return (
		<PageLayout>
			<div className={styles.page} style={themeVars}>
				<header className={styles.hero}>
					<p className={styles.eyebrow}>Account</p>
					<h1 className={styles.title}>Delete Your Account</h1>
					<p className={styles.subtitle}>
						You can permanently delete your BodhiStreams account and all
						associated data at any time. Here's how, and exactly what's removed.
					</p>
				</header>

				<div className={styles.notice}>
					<span className={styles.noticeIcon}>⚠️</span>
					<span>
						Deleting your account is <strong>permanent</strong> — it cannot be
						undone. We ask you to confirm by email first, and your account stays
						active until you do.
					</span>
				</div>

				<Section n="01" title="Delete from within the app">
					<p>The fastest way to delete your account:</p>
					<ol className={styles.steps}>
						<li>Open the BodhiStreams app and sign in.</li>
						<li>
							Go to the <strong>Profile</strong> tab.
						</li>
						<li>
							Scroll to the bottom and tap <strong>Delete account</strong>.
						</li>
						<li>
							Check your email — we send a confirmation link that expires in 24
							hours.
						</li>
						<li>
							Click <strong>Delete my account permanently</strong> in the email.
							Your account and data are deleted immediately after.
						</li>
					</ol>
				</Section>

				<Section n="02" title="Request deletion by email">
					<p>
						If you can no longer access the app, email us from the address
						associated with your account and we will delete it for you:
					</p>
					<div className={styles.callout}>
						<p>
							<strong>Email:</strong>{" "}
							<a
								href="mailto:support@bodhistreams.com?subject=Delete%20my%20account"
								className={styles.link}
							>
								support@bodhistreams.com
							</a>{" "}
							subject "Delete my account". We process these requests within 30
							days (typically much sooner).
						</p>
					</div>
				</Section>

				<Section n="03" title="What gets deleted">
					<p>
						Deleting your account permanently removes all personal data we hold
						about you, including:
					</p>
					<ul className={styles.list}>
						<li>Your account profile (name, email, authentication details).</li>
						<li>Your playlists and saved content.</li>
						<li>Listening history and recently played items.</li>
						<li>Registered devices and playback state.</li>
						<li>Search history and personalization data.</li>
						<li>Membership records associated with your account.</li>
					</ul>
					<p>
						Any active subscription is cancelled as part of the deletion so you
						are not billed again.
					</p>
				</Section>

				<Section n="04" title="What we retain, and for how long">
					<p>A limited set of records is retained only where required:</p>
					<ul className={styles.list}>
						<li>
							<strong>Payment / transaction records:</strong> retained as
							required by tax and accounting law, with your account link
							removed.
						</li>
						<li>
							<strong>Operational telemetry:</strong> any diagnostic logs expire
							automatically within 90 days.
						</li>
					</ul>
				</Section>

				<p className={styles.footer}>
					For anything related to your data or this process, contact us at{" "}
					<a href="mailto:support@bodhistreams.com" className={styles.link}>
						support@bodhistreams.com
					</a>
					.
				</p>
			</div>
		</PageLayout>
	);
};

export default DeleteAccount;
